import {
  C3D_HAIR,
  C3D_HAIR_COLORS,
  HAIR_CHANGE_PRICE,
  DEFAULT_APPEARANCE,
  type CharacterAppearance,
} from "@ghostland/shared";
import { appearanceStore } from "../items/appearanceStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { attachIdlePreview, type IdlePreview } from "../sprites/idlePreview.js";
import { injectTheme } from "./theme.js";
import { injectBillStyle, cashIcon } from "./pixelBills.js";

/**
 * The SALON — Coco the stylist changes the player's hair style + colour for a fee.
 * A card overlay with a live, hat-less preview on the left and the hair/colour steppers
 * on the right (reusing the same catalog the character creator does). Submitting sends the
 * choice to the server, which charges the fee and patches the synced appearance (so the
 * in-world billboard re-renders for everyone). The preview always hides the hat so the
 * hair is fully visible while choosing.
 */
const STYLE_ID = "ghostland-salon-style";

const CSS = `
#gl-salon-overlay {
  position: fixed; inset: 0; z-index: 33; display: none;
  align-items: center; justify-content: center;
  background: rgba(0,0,0,.72); color: var(--gl-text); font-family: var(--gl-font);
}
#gl-salon-overlay.open { display: flex; }
#gl-salon-card {
  width: min(560px, 94vw); max-height: 90vh; display: flex; flex-direction: column;
  background: var(--gl-panel); border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0,0,0,.7); overflow: hidden;
}
#gl-salon-head {
  display: flex; align-items: baseline; justify-content: space-between;
  padding: 16px 18px 12px; border-bottom: 1px solid var(--gl-border);
}
#gl-salon-head h2 { margin: 0; font-size: 17px; letter-spacing: 2px; }
#gl-salon-bal { font-size: 13px; color: var(--gl-muted); display: inline-flex; align-items: center; gap: 5px; }
#gl-salon-body { display: flex; gap: 12px; padding: 14px 18px; }
#gl-salon-stage {
  flex: none; width: 220px; height: 240px; display: flex; align-items: center; justify-content: center;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 10px;
}
#gl-salon-preview { image-rendering: pixelated; width: 200px; height: 200px; }
#gl-salon-controls { flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 18px; }
.gl-salon-group { display: flex; flex-direction: column; gap: 8px; }
.gl-salon-label { font-size: 12px; letter-spacing: 0.18em; color: var(--gl-muted); text-transform: uppercase; }
.gl-salon-row {
  display: flex; align-items: center; gap: 12px;
  border: 1px solid var(--gl-border); background: var(--gl-panel-2); border-radius: 8px; padding: 8px 10px;
}
.gl-salon-step {
  background: none; border: none; color: var(--gl-text); cursor: pointer; font-size: 18px; padding: 2px 8px; line-height: 1;
}
.gl-salon-step:hover:not(:disabled) { color: #fff; }
.gl-salon-step:disabled { color: var(--gl-border); cursor: default; }
.gl-salon-value { flex: 1; text-align: center; font-size: 14px; letter-spacing: 0.04em; }
#gl-salon-foot {
  display: flex; gap: 10px; justify-content: flex-end;
  padding: 12px 18px 16px; border-top: 1px solid var(--gl-border);
}
#gl-salon-cancel { padding: 9px 18px; cursor: pointer; border-radius: 6px; border: 1px solid var(--gl-border); background: var(--gl-panel-2); color: var(--gl-text); font-size: 12px; }
#gl-salon-cancel:hover { border-color: var(--gl-border-focus); }
#gl-salon-apply {
  padding: 9px 18px; cursor: pointer; border-radius: 6px; border: none;
  background: var(--gl-accent); color: var(--gl-accent-fg); font-size: 12px; font-weight: 700;
}
#gl-salon-apply:hover:not(:disabled) { background: var(--gl-accent-hover); }
#gl-salon-apply:disabled { opacity: .4; cursor: default; }
`;

export interface SalonActions {
  /** Submit a new hair style + colour to the server (it charges the fee + applies it). */
  setHair?(hair: number, hairColor: number): void;
}
let actions: SalonActions = {};
export function setSalonActions(a: SalonActions): void {
  actions = a;
}

const hairIds = [0, ...Object.keys(C3D_HAIR).map(Number)];

let overlay: HTMLDivElement | null = null;
let balEl: HTMLElement | null = null;
let applyBtn: HTMLButtonElement | null = null;
let previewCanvas: HTMLCanvasElement | null = null;
let preview: IdlePreview | null = null;
let unsub: (() => void) | null = null;
/** The working appearance being styled (a copy; only hair/hairColor change). */
let working: CharacterAppearance = { ...DEFAULT_APPEARANCE };

function ensureStyle(): void {
  injectTheme();
  injectBillStyle();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

function cycle(list: number[], cur: number, dir: number): number {
  const i = list.indexOf(cur);
  return list[(i + dir + list.length) % list.length];
}

/** Refresh the value labels + preview (hat hidden) from the working appearance. */
function render(): void {
  if (!overlay) return;
  const set = (row: string, text: string): void => {
    overlay!.querySelector(`[data-row="${row}"] .gl-salon-value`)!.textContent = text;
  };
  set("hair", working.hair === 0 ? "None" : C3D_HAIR[working.hair]?.label ?? "—");
  set("hairColor", working.hair === 0 ? "—" : C3D_HAIR_COLORS[working.hairColor]?.name ?? "—");
  // Disable the colour stepper while bald (no colour to pick).
  overlay.querySelectorAll<HTMLButtonElement>('[data-row="hairColor"] .gl-salon-step')
    .forEach((b) => { b.disabled = working.hair === 0; });
  preview?.setAppearance({ ...working, hat: 0, hatColor: 0 }); // always show the hair (no hat)
}

/** Update the cash readout + Apply affordability. */
function refreshAffordability(balance: number): void {
  if (balEl) balEl.textContent = balance.toLocaleString();
  if (applyBtn) applyBtn.disabled = balance < HAIR_CHANGE_PRICE;
}

function build(): HTMLDivElement {
  ensureStyle();
  const el = document.createElement("div");
  el.id = "gl-salon-overlay";
  el.innerHTML = `
    <div id="gl-salon-card">
      <div id="gl-salon-head">
        <h2>SALON</h2>
        <span id="gl-salon-bal">Cash: ${cashIcon()} <b id="gl-salon-bal-amt">0</b></span>
      </div>
      <div id="gl-salon-body">
        <div id="gl-salon-stage"><canvas id="gl-salon-preview" width="200" height="200"></canvas></div>
        <div id="gl-salon-controls">
          <div class="gl-salon-group">
            <span class="gl-salon-label">Hair</span>
            <div class="gl-salon-row" data-row="hair">
              <button class="gl-salon-step" data-dir="-1" type="button">‹</button>
              <span class="gl-salon-value"></span>
              <button class="gl-salon-step" data-dir="1" type="button">›</button>
            </div>
          </div>
          <div class="gl-salon-group">
            <span class="gl-salon-label">Hair colour</span>
            <div class="gl-salon-row" data-row="hairColor">
              <button class="gl-salon-step" data-dir="-1" type="button">‹</button>
              <span class="gl-salon-value"></span>
              <button class="gl-salon-step" data-dir="1" type="button">›</button>
            </div>
          </div>
        </div>
      </div>
      <div id="gl-salon-foot">
        <button id="gl-salon-cancel" type="button">Cancel</button>
        <button id="gl-salon-apply" type="button">Apply — ${cashIcon()} ${HAIR_CHANGE_PRICE.toLocaleString()}</button>
      </div>
    </div>`;
  el.addEventListener("click", (e) => { if (e.target === el) closeSalon(); });
  el.querySelector("#gl-salon-cancel")!.addEventListener("click", () => closeSalon());
  balEl = el.querySelector<HTMLElement>("#gl-salon-bal-amt");
  applyBtn = el.querySelector<HTMLButtonElement>("#gl-salon-apply");

  el.querySelectorAll<HTMLButtonElement>(".gl-salon-step").forEach((btn) => {
    btn.addEventListener("click", () => {
      const dir = Number(btn.dataset.dir) as 1 | -1;
      const row = btn.closest("[data-row]")!.getAttribute("data-row");
      if (row === "hair") working.hair = cycle(hairIds, working.hair, dir);
      else if (row === "hairColor" && working.hair > 0)
        working.hairColor = (working.hairColor + dir + C3D_HAIR_COLORS.length) % C3D_HAIR_COLORS.length;
      render();
    });
  });

  applyBtn!.addEventListener("click", () => {
    actions.setHair?.(working.hair, working.hairColor);
    closeSalon();
  });

  previewCanvas = el.querySelector<HTMLCanvasElement>("#gl-salon-preview")!;
  document.body.appendChild(el);
  return el;
}

export function showSalon(): void {
  if (!overlay) overlay = build();
  // Seed the working look from the player's current appearance (the server is authoritative).
  working = { ...appearanceStore.get() };
  if (!preview && previewCanvas) preview = attachIdlePreview(previewCanvas, { ...working, hat: 0, hatColor: 0 });
  render();
  overlay.classList.add("open");
  unsub?.();
  unsub = balanceStore.subscribe(refreshAffordability); // fires immediately with the current balance
}

export function closeSalon(): void {
  overlay?.classList.remove("open");
  unsub?.(); unsub = null;
  preview?.dispose(); preview = null; // stop the preview animation loop while closed
}

export function isSalonOpen(): boolean {
  return !!overlay?.classList.contains("open");
}
